import { useState } from "react";
import { ChevronRight, Folder, Home } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { Spinner } from "@/components/ui/Spinner";

import { useNodeList } from "../hooks";

interface Props {
  open: boolean;
  title: string;
  confirmLabel: string;
  submitting?: boolean;
  /** Node ids being moved/copied — hidden and non-enterable as destinations. */
  excludeIds?: Set<string>;
  onClose: () => void;
  onConfirm: (dest: string | null) => void;
}

/**
 * Browse the current user's OWN drive and pick a destination folder. Always
 * starts at my root, so it works as a target picker even while viewing someone
 * else's shared tree (copy-into-my-drive).
 */
export function FolderPickerDialog({
  open,
  title,
  confirmLabel,
  submitting,
  excludeIds,
  onClose,
  onConfirm,
}: Props) {
  const [parent, setParent] = useState<string | null>(null);
  const { data, isLoading } = useNodeList(parent);

  const folders = (data?.nodes ?? []).filter(
    (n) => n.node_type === "folder" && !excludeIds?.has(n.id),
  );
  const breadcrumb = data?.meta?.breadcrumb ?? [];

  function reset() {
    setParent(null);
    onClose();
  }

  return (
    <Modal open={open} onClose={reset} title={title} size="md">
      <div className="flex flex-col gap-3">
        <nav className="flex flex-wrap items-center gap-1 text-sm font-mono text-[var(--color-text-secondary)]">
          <button
            onClick={() => setParent(null)}
            className="inline-flex items-center gap-1 hover:text-[var(--color-primary)]"
          >
            <Home size={14} /> Moje pliki
          </button>
          {breadcrumb.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1">
              <ChevronRight size={13} className="text-[var(--color-text-muted)]" />
              <button
                onClick={() => setParent(c.id)}
                className="hover:text-[var(--color-primary)] truncate"
              >
                {c.name}
              </button>
            </span>
          ))}
        </nav>

        <div className="border border-[var(--color-border)] min-h-[220px] max-h-[320px] overflow-y-auto">
          {isLoading ? (
            <div className="py-10 flex justify-center">
              <Spinner />
            </div>
          ) : folders.length === 0 ? (
            <div className="py-10 text-center text-sm text-[var(--color-text-muted)]">
              Brak podfolderów tutaj.
            </div>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {folders.map((f) => (
                <li key={f.id}>
                  <button
                    onClick={() => setParent(f.id)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[var(--color-bg-base)]"
                  >
                    <Folder size={16} className="text-blue-500 flex-shrink-0" />
                    <span className="truncate text-sm flex-1">{f.name}</span>
                    <ChevronRight size={14} className="text-[var(--color-text-muted)]" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="secondary" size="sm" onClick={reset}>
            Anuluj
          </Button>
          <Button size="sm" disabled={submitting} onClick={() => onConfirm(parent)}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
